import React, { useEffect, useId, useState } from "react";
import { Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import {
  approveMaterialRequest,
  rejectMaterialRequest,
} from "../services/MaterialRequestService";
import type { MaterialRequest } from "../types/material-request.types";
import { invalidatePendingMaterialRequestCount } from "../materialRequestPendingCountInvalidate";
import { materialRequestDisplayCode } from "../utils/material-request.utils";

function axiosErrorMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  );
}

export interface MaterialRequestApproveRejectModalsProps {
  record: MaterialRequest | null | undefined;
  approveOpen: boolean;
  rejectOpen: boolean;
  onCloseApprove: () => void;
  onCloseReject: () => void;
  onSuccess: () => void | Promise<void>;
}

export default function MaterialRequestApproveRejectModals({
  record,
  approveOpen,
  rejectOpen,
  onCloseApprove,
  onCloseReject,
  onSuccess,
}: MaterialRequestApproveRejectModalsProps) {
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const rejectReasonFieldId = useId();

  useEffect(() => {
    if (rejectOpen) setRejectReason("");
  }, [rejectOpen]);

  const displayCode = materialRequestDisplayCode(record ?? {});

  const closeApprove = () => {
    if (approveSubmitting) return;
    onCloseApprove();
  };

  const closeReject = () => {
    if (rejectSubmitting) return;
    setRejectReason("");
    onCloseReject();
  };

  const handleApprove = async () => {
    if (!record?.id) return;
    setApproveSubmitting(true);
    try {
      const res = await approveMaterialRequest(record.id);
      if (res.success) {
        toast.success(res.message);
        invalidatePendingMaterialRequestCount();
        await Promise.resolve(onSuccess());
        onCloseApprove();
      } else {
        toast.error(res.message ?? "Approve failed.");
      }
    } catch (err) {
      toast.error(axiosErrorMessage(err, "Approve failed."));
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!record?.id) return;
    setRejectSubmitting(true);
    try {
      const res = await rejectMaterialRequest(record.id, {
        reason: rejectReason.trim() || undefined,
      });
      if (res.success) {
        toast.success(res.message);
        invalidatePendingMaterialRequestCount();
        await Promise.resolve(onSuccess());
        onCloseReject();
      } else {
        toast.error(res.message ?? "Reject failed.");
      }
    } catch (err) {
      toast.error(axiosErrorMessage(err, "Reject failed."));
    } finally {
      setRejectSubmitting(false);
    }
  };

  return (
    <>
      <ConfirmActionModal
        show={approveOpen}
        title="Approve material request"
        iconClass="ri-checkbox-circle-line"
        iconColor="text-success"
        heading="Confirm approval"
        description={`Request ${displayCode}.\n\nApproved requests can be fulfilled by issuing stock from a location. Review line quantities before you approve.`}
        confirmText={approveSubmitting ? "Approving…" : "Approve"}
        confirmColor="success"
        confirmDisabled={approveSubmitting}
        onConfirm={() => void handleApprove()}
        onClose={closeApprove}
      />
      <ConfirmActionModal
        show={rejectOpen}
        title="Reject material request"
        iconClass="ri-close-circle-line"
        iconColor="text-danger"
        heading="Reject this request?"
        description={
          <div>
            <p className="mb-3">
              Request <strong className="text-body">{displayCode}</strong>. This
              sets the status to <strong>Rejected</strong>.
            </p>
            <Label className="form-label text-body" for={rejectReasonFieldId}>
              Reason (optional)
            </Label>
            <Input
              id={rejectReasonFieldId}
              type="textarea"
              rows={4}
              placeholder="e.g. Stock not required, duplicate request…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={rejectSubmitting}
            />
          </div>
        }
        confirmText={rejectSubmitting ? "Rejecting…" : "Reject request"}
        confirmColor="danger"
        confirmDisabled={rejectSubmitting}
        onConfirm={() => void handleReject()}
        onClose={closeReject}
      />
    </>
  );
}
